import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import { useFormatText } from '@/hooks/useFormatText';
import { type IBulkUploadResult } from '@/types/class';

const hasAnyIssues = (result: IBulkUploadResult): boolean => {
  return (
    (result.errors?.length ?? 0) > 0 ||
    (result.duplicates_in_file?.length ?? 0) > 0 ||
    (result.duplicates_in_db?.length ?? 0) > 0 ||
    (result.email_failures?.length ?? 0) > 0
  );
};

interface IBulkUploadIssueRowProps {
  rowLabel?: string;
  title: string;
  description: string;
}

interface IBulkUploadIssueSectionProps {
  title: string;
  count: number;
  children: React.ReactNode;
}

const ROW_LABEL_KEY = 'classDetails.bulkUploadModal.result.row-label';

interface IBulkUploadReviewModalProps {
  isOpen: boolean;
  result: IBulkUploadResult | null;
  onClose: () => void;
}

export const BulkUploadReviewModal = ({
  isOpen,
  result,
  onClose,
}: IBulkUploadReviewModalProps) => {
  const { formatText } = useFormatText();

  if (!isOpen || !result) return null;

  const {
    total_rows,
    success_count,
    skipped_count,
    failed_count,
    errors,
    duplicates_in_file,
    duplicates_in_db,
    email_failures,
  } = result;

  const hasOnlySuccess = !hasAnyIssues(result);

  return (
    <CustomModal
      setIsOpen={onClose}
      onClose={onClose}
      position="right"
      isHeader
      heading={formatText('classDetails.bulkUploadModal.title')}
      isFooter
      confirmButtonProps={{
        label: formatText('common.actions.done'),
        onClick: onClose,
        className: 'ml-auto',
      }}
      dataTestId="bulk-upload-review-modal"
    >
      <div className="w-full space-y-4" data-testid="bulk-upload-result-view">
        {/* Summary banner */}
        <div
          className={`flex items-start gap-3 p-4 rounded-xl ${
            hasOnlySuccess ? 'bg-success-light-50' : 'bg-warning-light-50'
          }`}
        >
          <div
            className={`shrink-0 ${
              hasOnlySuccess
                ? 'text-success-secondary'
                : 'text-warning-secondary'
            }`}
          >
            {hasOnlySuccess ? (
              <IconCircleCheck size={20} />
            ) : (
              <IconAlertCircle size={20} />
            )}
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-primary">
              {formatText('classDetails.bulkUploadModal.result.title')}
            </p>
            <p className="text-sm text-neutral-secondary leading-5">
              {formatText('classDetails.bulkUploadModal.result.summary', {
                success: success_count,
                total: total_rows,
              })}
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-neutral-light-200 p-3">
            <p className="text-xs text-neutral-secondary">
              {formatText('classDetails.bulkUploadModal.result.stats.total')}
            </p>
            <p className="text-xl font-bold text-neutral-primary mt-1">
              {total_rows}
            </p>
          </div>
          <div className="rounded-xl border border-neutral-light-200 bg-success-light-50 p-3">
            <p className="text-xs text-success-secondary">
              {formatText('classDetails.bulkUploadModal.result.stats.uploaded')}
            </p>
            <p className="text-xl font-bold text-success-secondary mt-1">
              {success_count}
            </p>
          </div>
          <div className="rounded-xl border border-neutral-light-200 bg-warning-light-50 p-3">
            <p className="text-xs text-warning-secondary">
              {formatText('classDetails.bulkUploadModal.result.stats.skipped')}
            </p>
            <p className="text-xl font-bold text-warning-secondary mt-1">
              {skipped_count}
            </p>
          </div>
          <div className="rounded-xl border border-neutral-light-200 bg-error-light-50 p-3">
            <p className="text-xs text-error-secondary">
              {formatText('classDetails.bulkUploadModal.result.stats.failed')}
            </p>
            <p className="text-xl font-bold text-error-secondary mt-1">
              {failed_count}
            </p>
          </div>
        </div>

        {/* Validation errors */}
        {errors && errors?.length > 0 && (
          <BulkUploadIssueSection
            title={formatText(
              'classDetails.bulkUploadModal.result.sections.errors'
            )}
            count={errors.length}
          >
            {errors.map((item, idx) => (
              <BulkUploadIssueRow
                key={`err-${item?.row}-${item?.field}-${idx}`}
                rowLabel={formatText(ROW_LABEL_KEY, { row: item?.row })}
                title={item?.field}
                description={item?.message}
              />
            ))}
          </BulkUploadIssueSection>
        )}

        {/* Duplicates in file */}
        {duplicates_in_file && duplicates_in_file?.length > 0 && (
          <BulkUploadIssueSection
            title={formatText(
              'classDetails.bulkUploadModal.result.sections.duplicates-in-file'
            )}
            count={duplicates_in_file.length}
          >
            {duplicates_in_file.map((item, idx) => (
              <BulkUploadIssueRow
                key={`dupf-${item?.row}-${item?.field}-${idx}`}
                rowLabel={formatText(ROW_LABEL_KEY, { row: item?.row })}
                title={`${item?.field}: ${item?.value}`}
                description={item?.reason}
              />
            ))}
          </BulkUploadIssueSection>
        )}

        {/* Duplicates in DB */}
        {duplicates_in_db && duplicates_in_db?.length > 0 && (
          <BulkUploadIssueSection
            title={formatText(
              'classDetails.bulkUploadModal.result.sections.duplicates-in-db'
            )}
            count={duplicates_in_db?.length}
          >
            {duplicates_in_db?.map((item, idx) => (
              <BulkUploadIssueRow
                key={`dupd-${item?.row}-${item?.field}-${idx}`}
                rowLabel={formatText(ROW_LABEL_KEY, { row: item?.row })}
                title={`${item?.field}: ${item?.value}`}
                description={item?.reason}
              />
            ))}
          </BulkUploadIssueSection>
        )}

        {/* Email failures */}
        {email_failures && email_failures?.length > 0 && (
          <BulkUploadIssueSection
            title={formatText(
              'classDetails.bulkUploadModal.result.sections.email-failures'
            )}
            count={email_failures?.length}
          >
            {email_failures?.map((item, idx) => (
              <BulkUploadIssueRow
                key={`email-${item.email}-${idx}`}
                title={item.name}
                description={`${item.email} — ${item.error}`}
              />
            ))}
          </BulkUploadIssueSection>
        )}
      </div>
    </CustomModal>
  );
};

const BulkUploadIssueSection = ({
  title,
  count,
  children,
}: IBulkUploadIssueSectionProps) => (
  <div className="rounded-xl border border-neutral-light-200">
    <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-light-200">
      <h4 className="text-sm font-semibold text-neutral-primary">{title}</h4>
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-xs font-semibold text-neutral-primary">
        {count}
      </span>
    </div>
    <ul className="divide-y divide-neutral-light-200">{children}</ul>
  </div>
);

const BulkUploadIssueRow = ({
  rowLabel,
  title,
  description,
}: IBulkUploadIssueRowProps) => (
  <li className="flex items-start gap-3 px-4 py-3">
    {rowLabel && (
      <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-xs font-semibold text-neutral-secondary mt-0.5">
        {rowLabel}
      </span>
    )}
    <div className="flex flex-col gap-0.5 min-w-0">
      <p className="text-sm font-medium text-neutral-primary wrap-break-word">
        {title}
      </p>
      <p className="text-xs text-neutral-secondary wrap-break-word">
        {description}
      </p>
    </div>
  </li>
);
